import { Settings01Icon } from '@langchain/untitled-ui-icons';
import { FormControl, FormLabel, Option, Select } from '@mui/joy';

import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import useToast from '@/components/Toast/useToast';
import { Text } from '@/design-system';
import { Button } from '@/design-system/components/Button';
import {
  useOrgWorkspaces,
  useRoles,
  useSSOSettings,
  useUpdateSSOSettingsMutation,
} from '@/hooks/useSwr';
import { TenantSchema } from '@/types/schema';

import { WorkspaceSelect } from './InviteOrgMemberModal';

interface MemberSettingsFormValues {
  default_workspace_role_id: string;
  default_workspace_ids: string[];
}

export function MemberSettingsModal() {
  'use no memo';
  const [open, setOpen] = useState(false);

  const { data: ssoSettings } = useSSOSettings();
  const { data: workspaces } = useOrgWorkspaces();
  const { data: roles } = useRoles('workspace');
  const { createToast } = useToast();

  const updateSSOSettings = useUpdateSSOSettingsMutation({
    onSuccess: () => {
      createToast({
        title: 'Settings saved',
        description: 'Member settings updated successfully',
        type: 'success',
      });
      setOpen(false);
    },
    onError: () => {
      createToast({
        title: 'Error',
        description: 'Failed to update member settings',
        type: 'error',
      });
    },
  });

  const currentSettings = ssoSettings?.[0];

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm<MemberSettingsFormValues>({
    defaultValues: {
      default_workspace_role_id: '',
      default_workspace_ids: [],
    },
  });

  // Update form values when SSO settings data becomes available and only reset if the form hasn't been modified by the user
  // Only reset if the form hasn't been modified by the user
  useEffect(() => {
    if (currentSettings && !isDirty) {
      reset({
        default_workspace_role_id:
          currentSettings.default_workspace_role_id || '',
        default_workspace_ids: currentSettings.default_workspace_ids || [],
      });
    }
  }, [currentSettings, reset, isDirty]);

  const onSubmit = async (formData: MemberSettingsFormValues) => {
    if (!currentSettings?.id) return;

    await updateSSOSettings.trigger({
      json: {
        id: currentSettings.id,
        default_workspace_role_id: formData.default_workspace_role_id,
        default_workspace_ids: formData.default_workspace_ids,
      },
      templateUrlParams: {
        id: currentSettings.id,
      },
    });
  };

  const handleClose = () => {
    if (currentSettings) {
      reset({
        default_workspace_role_id:
          currentSettings.default_workspace_role_id || '',
        default_workspace_ids: currentSettings.default_workspace_ids || [],
      });
    }
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent title="New Member Settings" childrenClassName="gap-8">
          <Text variant="h3">
            Configure default settings for new members joining via SSO.
          </Text>

          {updateSSOSettings.error && (
            <ExpandableErrorAlert error={updateSSOSettings.error} />
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col items-stretch gap-8"
          >
            <div className="flex flex-col items-stretch gap-6">
              <FormControl>
                <FormLabel className="text-sm font-normal text-tertiary">
                  Default Workspace Role
                </FormLabel>
                <Controller
                  name="default_workspace_role_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select a role"
                      value={field.value || null}
                      onChange={(_, value) => field.onChange(value)}
                    >
                      {roles?.map((role) => (
                        <Option key={role.id} value={role.id}>
                          {role.display_name}
                        </Option>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Default Workspaces</FormLabel>
                <Controller
                  name="default_workspace_ids"
                  control={control}
                  render={({ field }) => {
                    const selectedWorkspaces =
                      workspaces?.filter((workspace) =>
                        field.value.includes(workspace.id)
                      ) || [];

                    const setSelectedWorkspaces = (
                      value: React.SetStateAction<TenantSchema[]>
                    ) => {
                      const newWorkspaces =
                        typeof value === 'function'
                          ? value(selectedWorkspaces)
                          : value;
                      field.onChange(
                        newWorkspaces.map((workspace) => workspace.id)
                      );
                    };

                    return (
                      <WorkspaceSelect
                        value={selectedWorkspaces}
                        onChange={setSelectedWorkspaces}
                      />
                    );
                  }}
                />
              </FormControl>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button type="submit" loading={updateSSOSettings.isMutating}>
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Button
        size="sm"
        color="primary"
        onClick={() => setOpen(true)}
        leftDecorator={Settings01Icon}
      >
        New Members
      </Button>
    </>
  );
}
